import React, { useEffect, useMemo, useState } from 'react';
import './editBoats.css';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import useStripePaymentGuard from '../../../payments/useStripePaymentGuard';
import { getCurrencyMeta } from '../../../../utils/currency';
import {
  buildMooringPublicMapView,
  summarizeMoorings,
} from '../../../teams/utils/moorings';
import { normalizeSailingBoatStatus, safeSailingBoatLabel } from '../../../teams/utils/sailingFleet';

function cleanText(value) {
  return String(value || '').trim();
}

function cleanNumber(value) {
  if (value === '' || value == null) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function compareBoats(a, b) {
  return (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });
}

export function isAssignableMooringSpace(space = {}) {
  return String(space.shape || 'rect') !== 'label';
}

export function spaceLabel(space, areasById) {
  const area = areasById.get(space.areaId);
  return [area?.name, space.label].filter(Boolean).join(' · ') || space.label || 'Mooring space';
}

export default function EditSailingBoatsForm({
  isOpen,
  onClose,
  onSave,
  teamId,
  allTags = [],
  focusBoatId = null,
}) {
  const [boats, setBoats] = useState([]);
  const [areas, setAreas] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(!focusBoatId);
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showClubBoatNamesOnMooringMap, setShowClubBoatNamesOnMooringMap] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [currency, setCurrency] = useState('GBP');
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(teamId);

  useEffect(() => {
    setShowAll(!focusBoatId);
  }, [focusBoatId]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [boatsSnap, areasSnap, spacesSnap, teamSnap, stripeSetupSnap] = await Promise.all([
          getDocs(collection(db, 'teams', teamId, 'sailingBoats')),
          getDocs(collection(db, 'teams', teamId, 'mooringAreas')),
          getDocs(collection(db, 'teams', teamId, 'mooringSpaces')),
          getDoc(doc(db, 'teams', teamId)),
          getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')),
        ]);
        if (cancelled) return;
        const rows = boatsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })).sort(compareBoats);
        setBoats(rows);
        setAreas(areasSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setSpaces(spacesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setShowClubBoatNamesOnMooringMap(
          teamSnap.data()?.modules?.sailing?.boats?.showClubBoatNamesOnMooringMap === true
        );
        setCurrency(String(stripeSetupSnap.data()?.currency || 'GBP').toUpperCase());
        setOriginalIds(new Set(rows.map((row) => row.id)));
        setDeletedIds(new Set());
        setSearchQuery('');
        setPendingRemoval(null);
        setError('');
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Could not load club boats.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);

  const areasById = useMemo(() => new Map(areas.map((area) => [area.id, area])), [areas]);
  const sortedSpaces = useMemo(
    () => spaces
      .filter(isAssignableMooringSpace)
      .sort((a, b) => spaceLabel(a, areasById).localeCompare(spaceLabel(b, areasById), undefined, { sensitivity: 'base' })),
    [areasById, spaces]
  );
  const assignableSpaceIds = useMemo(() => new Set(sortedSpaces.map((space) => space.id)), [sortedSpaces]);

  const visibleBoats = useMemo(() => {
    const indexed = boats
      .map((boat, index) => ({ boat, index }))
      .filter(({ boat }) => !boat.id || !deletedIds.has(boat.id));
    const focused = (!focusBoatId || showAll)
      ? indexed
      : indexed.filter(({ boat }) => boat.id === focusBoatId);
    const query = cleanText(searchQuery).toLocaleLowerCase();
    if (!query) return focused;
    return focused.filter(({ boat }) => [
      boat.name,
      boat.type,
      boat.className,
      boat.sailNumber,
      boat.status,
    ].some((value) => String(value || '').toLocaleLowerCase().includes(query)));
  }, [boats, deletedIds, focusBoatId, searchQuery, showAll]);

  const deletedBoats = useMemo(
    () => boats.filter((boat) => boat.id && deletedIds.has(boat.id)),
    [boats, deletedIds]
  );
  const activeBoatCount = boats.length - deletedBoats.length;

  const patchBoat = (index, patch) => {
    setBoats((current) => current.map((boat, idx) => (idx === index ? { ...boat, ...patch } : boat)));
  };

  const toggleTag = (index, tagName) => {
    setBoats((current) => current.map((boat, idx) => {
      if (idx !== index) return boat;
      const next = new Set(Array.isArray(boat.requiredTags) ? boat.requiredTags : []);
      next.has(tagName) ? next.delete(tagName) : next.add(tagName);
      return { ...boat, requiredTags: Array.from(next) };
    }));
  };

  const addBoat = () => {
    setSearchQuery('');
    setBoats((current) => [...current, {
      name: '',
      type: 'Dinghy',
      className: '',
      sailNumber: '',
      status: 'active',
      capacity: '',
      length: '',
      beam: '',
      draft: '',
      requiredTags: [],
      homeMooringSpaceId: '',
      currentMooringSpaceId: '',
      maintenanceStatus: '',
      notes: '',
      visibility: 'members',
      payment: {
        required: false,
        pricingMode: 'flat',
        amountMinor: 0,
        collectionTiming: 'at_booking',
        tagMode: 'all',
        paymentTags: [],
        blockFutureBookings: false,
      },
    }]);
  };

  const removeBoat = (index) => {
    setPendingRemoval({ boat: boats[index], index });
  };

  const confirmRemoveBoat = () => {
    if (!pendingRemoval) return;
    const { boat, index } = pendingRemoval;
    if (boat?.id) {
      setDeletedIds((ids) => new Set(ids).add(boat.id));
    } else {
      setBoats((current) => current.filter((_, idx) => idx !== index));
    }
    setPendingRemoval(null);
  };

  const restoreBoat = (boatId) => {
    setDeletedIds((ids) => {
      const next = new Set(ids);
      next.delete(boatId);
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!teamId) return;
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        'modules.sailing.boats.showClubBoatNamesOnMooringMap': showClubBoatNamesOnMooringMap,
      });
      const currentIds = new Set(boats.filter((boat) => boat.id).map((boat) => boat.id));
      const toDelete = new Set([...originalIds].filter((id) => !currentIds.has(id) || deletedIds.has(id)));
      for (const id of toDelete) {
        await deleteDoc(doc(db, 'teams', teamId, 'sailingBoats', id));
      }
      const savedBoats = [];
      for (const boat of boats) {
        if (boat.id && deletedIds.has(boat.id)) continue;
        if (boat.payment?.required === true && Math.round(Number(boat.payment?.amountMinor || 0)) <= 0) {
          throw new Error(`Add a price for ${cleanText(boat.name) || 'each paid boat'}.`);
        }
        if (boat.payment?.required === true && boat.payment?.tagMode === 'only_tags' && (!Array.isArray(boat.payment?.paymentTags) || boat.payment.paymentTags.length === 0)) {
          throw new Error(`Choose at least one paying tag for ${cleanText(boat.name) || 'each tag-limited boat'}.`);
        }
        const homeMooringSpaceId = cleanText(boat.homeMooringSpaceId);
        const currentMooringSpaceId = cleanText(boat.currentMooringSpaceId);
        const payload = {
          name: cleanText(boat.name),
          type: cleanText(boat.type) || 'Boat',
          className: cleanText(boat.className),
          sailNumber: cleanText(boat.sailNumber),
          status: normalizeSailingBoatStatus(boat.status),
          capacity: cleanNumber(boat.capacity),
          length: cleanNumber(boat.length),
          beam: cleanNumber(boat.beam),
          draft: cleanNumber(boat.draft),
          requiredTags: Array.isArray(boat.requiredTags) ? boat.requiredTags.filter(Boolean) : [],
          tagsAllowed: Array.isArray(boat.requiredTags) ? boat.requiredTags.filter(Boolean) : [],
          homeMooringSpaceId: assignableSpaceIds.has(homeMooringSpaceId) ? homeMooringSpaceId : '',
          currentMooringSpaceId: assignableSpaceIds.has(currentMooringSpaceId) ? currentMooringSpaceId : '',
          maintenanceStatus: cleanText(boat.maintenanceStatus),
          notes: cleanText(boat.notes),
          visibility: boat.visibility === 'admins' ? 'admins' : 'members',
          payment: {
            required: boat.payment?.required === true,
            pricingMode: boat.payment?.pricingMode === 'hourly' ? 'hourly' : 'flat',
            amountMinor: Math.max(0, Math.round(Number(boat.payment?.amountMinor || 0))),
            collectionTiming: ['at_booking', 'on_assignment', 'after_booking'].includes(boat.payment?.collectionTiming)
              ? boat.payment.collectionTiming
              : 'at_booking',
            tagMode: ['all', 'exempt_tags', 'only_tags'].includes(boat.payment?.tagMode)
              ? boat.payment.tagMode
              : 'all',
            paymentTags: Array.isArray(boat.payment?.paymentTags) ? boat.payment.paymentTags.filter(Boolean) : [],
            blockFutureBookings: boat.payment?.blockFutureBookings === true,
          },
          updatedAt: serverTimestamp(),
        };
        if (boat.id) {
          await updateDoc(doc(db, 'teams', teamId, 'sailingBoats', boat.id), payload);
          savedBoats.push({ id: boat.id, ...payload });
        } else {
          const boatRef = await addDoc(collection(db, 'teams', teamId, 'sailingBoats'), {
            ...payload,
            createdAt: serverTimestamp(),
          });
          savedBoats.push({ id: boatRef.id, ...payload });
        }
      }
      await syncMooringSpaceBoatLinks(savedBoats, toDelete);
      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save boats.');
    } finally {
      setSaving(false);
    }
  };

  async function syncMooringSpaceBoatLinks(savedBoats, deletedBoatIds = new Set()) {
    const assignableSpaces = spaces.filter(isAssignableMooringSpace);
    if (assignableSpaces.length === 0) return;

    const desiredBySpaceId = new Map();
    const managedBoatIds = new Set(deletedBoatIds);
    savedBoats.forEach((boat) => {
      managedBoatIds.add(boat.id);
      const targetSpaceId = boat.currentMooringSpaceId || boat.homeMooringSpaceId || '';
      if (!targetSpaceId) return;
      if (desiredBySpaceId.has(targetSpaceId)) {
        throw new Error('Only one club boat can be linked to each mooring space.');
      }
      desiredBySpaceId.set(targetSpaceId, boat);
    });

    const nextSpaces = [];
    const updates = [];
    assignableSpaces.forEach((space) => {
      const desiredBoat = desiredBySpaceId.get(space.id);
      const currentlyManaged = managedBoatIds.has(cleanText(space.clubBoatId));
      let patch = null;
      if (desiredBoat) {
        patch = {
          clubBoatId: desiredBoat.id,
          clubBoatLabel: safeSailingBoatLabel(desiredBoat),
          status: 'occupied',
        };
      } else if (currentlyManaged) {
        patch = {
          clubBoatId: '',
          clubBoatLabel: '',
          status: space.status === 'occupied' ? 'vacant' : space.status,
        };
      }

      if (!patch) {
        nextSpaces.push(space);
        return;
      }

      const nextSpace = { ...space, ...patch };
      nextSpaces.push(nextSpace);
      if (
        cleanText(space.clubBoatId) !== patch.clubBoatId
        || cleanText(space.clubBoatLabel) !== patch.clubBoatLabel
        || String(space.status || 'vacant') !== patch.status
      ) {
        updates.push(updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', space.id), {
          ...patch,
          updatedAt: serverTimestamp(),
        }));
      }
    });

    if (updates.length > 0) await Promise.all(updates);
    await syncMooringPublicViews(nextSpaces, showClubBoatNamesOnMooringMap);
  }

  async function syncMooringPublicViews(nextAssignableSpaces, showClubBoatNames) {
    const nextSpacesById = new Map(nextAssignableSpaces.map((space) => [space.id, space]));
    const mergedSpaces = spaces.map((space) => nextSpacesById.get(space.id) || space);
    const publicSpaces = mergedSpaces.map((space) => (
      space.clubBoatId
        ? {
          ...space,
          clubBoatLabel: showClubBoatNames ? (space.clubBoatLabel || 'Club boat') : 'Club boat',
        }
        : space
    ));
    const summary = summarizeMoorings({ spaces: publicSpaces });
    const publicMap = buildMooringPublicMapView(areas, publicSpaces);
    await Promise.all([
      setDoc(doc(db, 'teams', teamId, 'mooringPublicViews', 'summary'), {
        total: summary.total,
        vacant: summary.vacant,
        occupied: summary.occupied,
        reserved: summary.reserved,
        maintenanceBlocked: summary.maintenanceBlocked,
        areas: areas.length,
        updatedAt: serverTimestamp(),
      }, { merge: true }),
      setDoc(doc(db, 'teams', teamId, 'mooringPublicViews', 'map'), {
        ...publicMap,
        updatedAt: serverTimestamp(),
      }, { merge: true }),
    ]);
  }

  if (!isOpen) return null;

  const currencyMeta = getCurrencyMeta(currency);
  const minorFactor = 10 ** Number(currencyMeta.decimals || 0);
  const paymentFor = (boat) => ({
    required: false,
    pricingMode: 'flat',
    amountMinor: 0,
    collectionTiming: 'at_booking',
    tagMode: 'all',
    paymentTags: [],
    blockFutureBookings: false,
    ...(boat.payment || {}),
  });
  const patchPayment = (index, patch) => patchBoat(index, { payment: { ...paymentFor(boats[index] || {}), ...patch } });
  const togglePaymentTag = (index, tagName) => {
    const payment = paymentFor(boats[index] || {});
    const next = new Set(Array.isArray(payment.paymentTags) ? payment.paymentTags : []);
    next.has(tagName) ? next.delete(tagName) : next.add(tagName);
    patchPayment(index, { paymentTags: Array.from(next) });
  };

  return (
    <form className="modal-form sailing-boats-modal" onSubmit={handleSubmit}>
      <header className="sailing-boats-modal__header">
        <h2>Club Boats</h2>
        <p className="text-muted">Track club-owned sailing boats, restrictions, and their mooring spaces.</p>
      </header>

      <div className="sailing-boats-modal__body">
        {error && <p className="form-error" role="alert">{error}</p>}

        {loading ? <p className="text-muted">Loading boats…</p> : (
          <>
          <section className="card sailing-boats-modal__map-setting" aria-labelledby="club-boat-map-setting">
            <div>
              <h3 id="club-boat-map-setting">Member mooring maps</h3>
              <p className="text-muted">Choose whether members see each linked club boat’s name or the generic label “Club boat”.</p>
            </div>
            <label className="form-toggle-row">
              <span>Show club boat names</span>
              <input
                type="checkbox"
                checked={showClubBoatNamesOnMooringMap}
                onChange={(event) => setShowClubBoatNamesOnMooringMap(event.target.checked)}
              />
            </label>
          </section>

          {deletedBoats.length > 0 && (
            <section className="sailing-boats-modal__pending-deletions" aria-labelledby="pending-boat-deletions">
              <div>
                <h3 id="pending-boat-deletions">
                  {deletedBoats.length} boat{deletedBoats.length === 1 ? '' : 's'} will be deleted
                </h3>
                <p>Deletion only happens when you save. Restore any boat removed by mistake.</p>
              </div>
              <div className="sailing-boats-modal__restore-list">
                {deletedBoats.map((boat) => (
                  <button
                    key={boat.id}
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => restoreBoat(boat.id)}
                  >
                    Restore {cleanText(boat.name) || 'unnamed boat'}
                  </button>
                ))}
              </div>
            </section>
          )}

          {focusBoatId && !showAll && (
            <p className="text-muted boat-admin-focus-note">
              Editing selected boat.
              <button type="button" className="btn btn--secondary btn--xs" onClick={() => setShowAll(true)} style={{ marginLeft: 8 }}>
                Show all boats
              </button>
            </p>
          )}

          {(showAll || !focusBoatId) && activeBoatCount > 0 && (
            <div className="sailing-boats-modal__toolbar">
              <label className="form-label sailing-boats-modal__search">
                Search boats
                <input
                  className="input"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Name, class, sail number, or status"
                />
              </label>
              <span className="text-muted" aria-live="polite">
                {visibleBoats.length} of {activeBoatCount} boat{activeBoatCount === 1 ? '' : 's'}
              </span>
            </div>
          )}

          {visibleBoats.length === 0 && searchQuery && (
            <p className="text-muted sailing-boats-modal__empty">No boats match “{searchQuery}”.</p>
          )}

          {visibleBoats.map(({ boat, index }) => (
            <section key={boat.id || `new-${index}`} className="card boat-admin-card">
              <div className="sailing-boat-fields">
                <h3>Boat details</h3>
                <div className="sailing-boat-fields__grid">
                  <label className="form-label">Name<input className="input" value={boat.name || ''} onChange={(event) => patchBoat(index, { name: event.target.value })} /></label>
                  <label className="form-label">Type<input className="input" value={boat.type || ''} onChange={(event) => patchBoat(index, { type: event.target.value })} placeholder="Dinghy, keelboat, RIB" /></label>
                  <label className="form-label">Class<input className="input" value={boat.className || ''} onChange={(event) => patchBoat(index, { className: event.target.value })} placeholder="Wayfarer, Laser" /></label>
                  <label className="form-label">Sail / reg no.<input className="input" value={boat.sailNumber || ''} onChange={(event) => patchBoat(index, { sailNumber: event.target.value })} /></label>
                </div>
              </div>

              <div className="sailing-boat-fields">
                <h3>Status &amp; specifications</h3>
                <div className="sailing-boat-fields__grid sailing-boat-fields__grid--specifications">
                  <label className="form-label">Status<select className="input" value={boat.status || 'active'} onChange={(event) => patchBoat(index, { status: event.target.value })}><option value="active">Active</option><option value="maintenance">Maintenance</option><option value="retired">Retired</option></select></label>
                  <label className="form-label">Capacity<input className="input" type="number" min="0" value={boat.capacity ?? ''} onChange={(event) => patchBoat(index, { capacity: event.target.value })} /></label>
                  <label className="form-label">Length (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={boat.length ?? ''} onChange={(event) => patchBoat(index, { length: event.target.value })} /></label>
                  <label className="form-label">Beam (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={boat.beam ?? ''} onChange={(event) => patchBoat(index, { beam: event.target.value })} /></label>
                  <label className="form-label">Draft (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={boat.draft ?? ''} onChange={(event) => patchBoat(index, { draft: event.target.value })} /></label>
                </div>
              </div>

              <div className="sailing-boat-fields">
                <h3>Moorings &amp; visibility</h3>
                <div className="sailing-boat-fields__grid sailing-boat-fields__grid--moorings">
                  <label className="form-label">Home mooring<select className="input" value={boat.homeMooringSpaceId || ''} onChange={(event) => patchBoat(index, { homeMooringSpaceId: event.target.value })}><option value="">No home mooring</option>{sortedSpaces.map((space) => <option key={space.id} value={space.id}>{spaceLabel(space, areasById)}</option>)}</select></label>
                  <label className="form-label">Current mooring<select className="input" value={boat.currentMooringSpaceId || ''} onChange={(event) => patchBoat(index, { currentMooringSpaceId: event.target.value })}><option value="">No current mooring</option>{sortedSpaces.map((space) => <option key={space.id} value={space.id}>{spaceLabel(space, areasById)}</option>)}</select></label>
                  <label className="form-label">Member visibility<select className="input" value={boat.visibility || 'members'} onChange={(event) => patchBoat(index, { visibility: event.target.value })}><option value="members">Can appear to members</option><option value="admins">Admin only</option></select></label>
                </div>
              </div>

              <div className="sailing-boat-fields">
                <h3>Notes</h3>
                <div className="sailing-boat-fields__grid sailing-boat-fields__grid--notes">
                  <label className="form-label">Maintenance note<input className="input" value={boat.maintenanceStatus || ''} onChange={(event) => patchBoat(index, { maintenanceStatus: event.target.value })} /></label>
                  <label className="form-label">Private notes<textarea className="input" rows={3} value={boat.notes || ''} onChange={(event) => patchBoat(index, { notes: event.target.value })} /></label>
                </div>
              </div>

              {allTags.length > 0 && (
                <div className="boat-admin-tags">
                  <h3>Required booking tags</h3>
                  <div className="boat-admin-tag-grid">
                    {allTags.map((tag) => (
                      <label key={tag.name} className="boat-admin-tag-item">
                        <input type="checkbox" checked={Array.isArray(boat.requiredTags) && boat.requiredTags.includes(tag.name)} onChange={() => toggleTag(index, tag.name)} />
                        <span>{tag.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <section className="boat-payment-settings" aria-label={`Payment settings for ${cleanText(boat.name) || 'boat'}`}>
                <label className="form-toggle-row">
                  <span>
                    <strong>Require payment to book</strong>
                    <small className="text-muted">Existing and disabled boats remain free.</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={paymentFor(boat).required}
                    onChange={(event) => {
                      if (!event.target.checked) {
                        patchPayment(index, { required: false });
                        return;
                      }
                      requireStripePayment({
                        feature: 'sailing_boat_payments',
                        featureLabel: 'Paid sailing boat bookings',
                        returnTarget: 'sailing_boat_payments',
                        resumeEntity: boat.id || 'new',
                        onReady: () => patchPayment(index, { required: true }),
                      });
                    }}
                  />
                </label>

                {paymentFor(boat).required && (
                  <div className="boat-payment-settings__body">
                    <div className="form-grid boat-payment-settings__grid">
                      <label className="form-label">Pricing
                        <select className="input" value={paymentFor(boat).pricingMode} onChange={(event) => patchPayment(index, { pricingMode: event.target.value })}>
                          <option value="flat">Flat price</option>
                          <option value="hourly">Hourly rate</option>
                        </select>
                      </label>
                      <label className="form-label">{paymentFor(boat).pricingMode === 'hourly' ? 'Price per hour' : 'Price'} ({currency})
                        <span className="boat-payment-money-input">
                          <span>{currencyMeta.symbol}</span>
                          <input
                            className="input"
                            type="number"
                            min="0"
                            step={currencyMeta.decimals ? '0.01' : '1'}
                            value={(Number(paymentFor(boat).amountMinor || 0) / minorFactor).toFixed(currencyMeta.decimals || 0)}
                            onChange={(event) => patchPayment(index, { amountMinor: Math.max(0, Math.round(Number(event.target.value || 0) * minorFactor)) })}
                          />
                        </span>
                      </label>
                      <label className="form-label">Take payment
                        <select className="input" value={paymentFor(boat).collectionTiming} onChange={(event) => patchPayment(index, { collectionTiming: event.target.value })}>
                          <option value="at_booking">When the request is made</option>
                          <option value="on_assignment">When the boat is assigned</option>
                          <option value="after_booking">After the booking ends</option>
                        </select>
                      </label>
                      <label className="form-label">Who pays
                        <select className="input" value={paymentFor(boat).tagMode} onChange={(event) => patchPayment(index, { tagMode: event.target.value, paymentTags: event.target.value === 'all' ? [] : paymentFor(boat).paymentTags })}>
                          <option value="all">All eligible members</option>
                          <option value="exempt_tags">All except selected tags</option>
                          <option value="only_tags">Only selected tags</option>
                        </select>
                      </label>
                    </div>
                    {paymentFor(boat).tagMode !== 'all' && (
                      <div className="boat-admin-tags">
                        <div className="text-muted">{paymentFor(boat).tagMode === 'exempt_tags' ? 'Tags exempt from payment' : 'Tags that must pay'}</div>
                        <div className="boat-admin-tag-grid">
                          {allTags.map((tag) => (
                            <label key={`payment-${tag.name}`} className="boat-admin-tag-item">
                              <input type="checkbox" checked={paymentFor(boat).paymentTags.includes(tag.name)} onChange={() => togglePaymentTag(index, tag.name)} />
                              <span>{tag.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="form-toggle-row boat-payment-settings__block">
                      <span>
                        <strong>Block new sailing boat requests while this charge is unpaid</strong>
                        <small className="text-muted">The block starts as soon as an assignment payment is due, or when an after-booking payment reaches the scheduled end.</small>
                      </span>
                      <input type="checkbox" checked={paymentFor(boat).blockFutureBookings} onChange={(event) => patchPayment(index, { blockFutureBookings: event.target.checked })} />
                    </label>
                  </div>
                )}
              </section>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => removeBoat(index)}
                >
                  Remove boat…
                </button>
              </div>
            </section>
          ))}

          {(showAll || !focusBoatId) && (
            <button type="button" className="btn btn--secondary sailing-boats-modal__add" onClick={addBoat}>Add club boat</button>
          )}
          </>
        )}
      </div>

      <div className="form-actions sailing-boats-modal__footer">
        <button type="button" className="btn btn--secondary" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn--primary" disabled={saving || loading}>{saving ? 'Saving…' : 'Save boats'}</button>
      </div>

      {pendingRemoval && (
        <div className="sailing-boats-modal__confirm-backdrop" onClick={() => setPendingRemoval(null)}>
          <section
            className="sailing-boats-modal__confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="remove-club-boat-title"
            aria-describedby="remove-club-boat-description"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="remove-club-boat-title">
              {pendingRemoval.boat?.id ? 'Remove this club boat?' : 'Discard this new boat?'}
            </h3>
            <p id="remove-club-boat-description">
              {pendingRemoval.boat?.id
                ? `“${cleanText(pendingRemoval.boat?.name) || 'Unnamed boat'}” will be marked for deletion and its linked mooring will be cleared when you save.`
                : 'This unsaved boat will be discarded. No saved boat records will be affected.'}
            </p>
            {pendingRemoval.boat?.id && (
              <p className="text-muted">You can still restore it from the deletion notice before saving.</p>
            )}
            <div className="form-actions">
              <button type="button" className="btn btn--secondary" onClick={() => setPendingRemoval(null)}>Keep boat</button>
              <button type="button" className="btn btn--danger" onClick={confirmRemoveBoat}>
                {pendingRemoval.boat?.id ? 'Mark for deletion' : 'Discard new boat'}
              </button>
            </div>
          </section>
        </div>
      )}
      {stripeGuardDialog}
    </form>
  );
}
